import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import type { SearchFilters as SearchFiltersType } from '@/types/research';
import { useResearchStore } from '@/stores/researchStore';
import { Header } from '@/components/Header';
import { SearchFilters } from '@/components/research/SearchFilters';
import { LeadsTable } from '@/components/research/LeadsTable';
import {
  LinkedInScrapeModal,
  LinkedInResultsModal,
  DataFieldsInfoModal,
  ScheduleModal,
  SaveSearchModal,
  SavedSearchesDrawer,
} from '@/components/research/ResearchModals';
import {
  searchLeads,
  researchContact,
  getResearchStatus,
  exportLeadsToCSV,
  listSavedSearches,
  listInstantlyCampaignOptions,
  updateSavedSearchPage,
  type InstantlyCampaignOption,
} from '@/services/research';
import { deductCredits, getCreditBalances } from '@/services/credits';
import { API_CONFIG, FEATURE_FLAGS } from '@/services/api';
import { getAccountSettings, setGhlSendAsLocation } from '@/services/account';

const RESEARCH_CREDIT_COST = 1;
const LIST_ENTITY_TYPE = 'contact';
const DEFAULT_LIST_STORAGE_KEY = 'profitibull_default_contact_list';
const SUPER_SCRAPE_STORAGE_KEY = 'profitibull_super_scrape_enabled';
const PEPAI_RESEARCH_STORAGE_KEY = 'profitibull_pepai_research_enabled';
const PEPAI_CAMPAIGN_STORAGE_KEY = 'profitibull_pepai_campaign_id';

type CsvImportRow = {
  name: string;
  company: string;
  industry: string;
  website: string;
  keywords: string[];
};

type CsvRawData = {
  headers: string[];
  rows: string[][];
};

type CsvMappingMode = 'structured' | 'keyword';

type CsvMapping = {
  mode: CsvMappingMode;
  nameColumn: string;
  companyColumn: string;
  industryColumn: string;
  websiteColumn: string;
  keywordColumn: string;
};

type ResearchPipelineResult = Awaited<ReturnType<typeof researchContact>>;

function firstNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  return '';
}

function safeStorageGet(key: string): string {
  try {
    return localStorage.getItem(key) || '';
  } catch (error) {
    console.warn('localStorage get failed', { key, error });
    return '';
  }
}

function safeStorageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn('localStorage set failed', { key, error });
  }
}

function safeStorageRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('localStorage remove failed', { key, error });
  }
}

export function Research() {
  const { userEmail, isAuthenticated, accessPepaiResearch, addScrapeJob, updateScrapeJob, isAdmin, adminToken } = useAppStore();
  const [dataFieldsModalOpen, setDataFieldsModalOpen] = useState(false);
  const [csvStatus, setCsvStatus] = useState<string | null>(null);
  const [csvImportStatus, setCsvImportStatus] = useState<string | null>(null);
  const [csvImportCount, setCsvImportCount] = useState<number | null>(null);
  const [csvImportResults, setCsvImportResults] = useState<number | null>(null);
  const [csvConfirmOpen, setCsvConfirmOpen] = useState(false);
  const [csvConfirmCount, setCsvConfirmCount] = useState(0);
  const [csvPendingRows, setCsvPendingRows] = useState<CsvImportRow[]>([]);
  const [csvRawData, setCsvRawData] = useState<CsvRawData | null>(null);
  const [csvMapOpen, setCsvMapOpen] = useState(false);
  const [csvMapping, setCsvMapping] = useState<CsvMapping>({
    mode: 'structured',
    nameColumn: '',
    companyColumn: '',
    industryColumn: '',
    websiteColumn: '',
    keywordColumn: '',
  });
  const [defaultList, setDefaultList] = useState(
    () =>
      safeStorageGet(DEFAULT_LIST_STORAGE_KEY) ||
      safeStorageGet('profitibull_default_list') ||
      ''
  );
  const [userLists, setUserLists] = useState<string[]>([]);
  const [csvConfirmList, setCsvConfirmList] = useState('');
  const [activeListName, setActiveListName] = useState(
    () =>
      safeStorageGet(DEFAULT_LIST_STORAGE_KEY) ||
      safeStorageGet('profitibull_default_list') ||
      ''
  );
  const [superScrapeEnabled, setSuperScrapeEnabled] = useState(
    () => safeStorageGet(SUPER_SCRAPE_STORAGE_KEY) === 'true'
  );
  const [pepaiResearchEnabled, setPepaiResearchEnabled] = useState(
    () => safeStorageGet(PEPAI_RESEARCH_STORAGE_KEY) === 'true'
  );
  const [instantlyCampaigns, setInstantlyCampaigns] = useState<InstantlyCampaignOption[]>([]);
  const [selectedInstantlyCampaignId, setSelectedInstantlyCampaignId] = useState(
    () => safeStorageGet(PEPAI_CAMPAIGN_STORAGE_KEY) || ''
  );
  const [ghlSendAsLocationId, setGhlSendAsLocationId] = useState('');
  const [ghlTargetOptions, setGhlTargetOptions] = useState<Array<{ locationId: string; label: string }>>([]);
  const [ghlSendAsSaving, setGhlSendAsSaving] = useState(false);
  const canChooseGhlTarget = isAdmin || userEmail.toLowerCase().endsWith('@profitibull.com');
  const targetGhlLocationId = useMemo(() => {
    if (!canChooseGhlTarget) return undefined;
    const value = String(ghlSendAsLocationId || '').trim();
    if (!value) return undefined;
    return ghlTargetOptions.some((option) => option.locationId === value) ? value : undefined;
  }, [canChooseGhlTarget, ghlSendAsLocationId, ghlTargetOptions]);
  const [createListModalOpen, setCreateListModalOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [setNewListAsDefault, setSetNewListAsDefault] = useState(true);
  const [contactCredits, setContactCredits] = useState(0);
  const csvInputRef = useRef<HTMLInputElement | null>(null);

  const refreshContactCredits = useCallback(async () => {
    if (!userEmail) return 0;
    const balances = await getCreditBalances(userEmail);
    const next = Number(balances.contactCredits ?? 0);
    setContactCredits(next);
    return next;
  }, [userEmail]);
  const {
    filters,
    leads,
    totalResults,
    currentPage,
    perPage,
    loading,
    selectedLeads,
    researchedContacts,
    pendingSearch,
    activeSavedSearchId,
    setFilters,
    setLeads,
    setCurrentPage,
    setLoading,
    addRecentSearch,
    setSavedSearches,
    updateSavedSearchPage: updateSavedSearchPageLocal,
    getSelectedIndices,
    saveResearchedContact,
    getResearchedContactForLead,
    setPendingSearch,
    setActiveSavedSearchId,
  } = useResearchStore();
  const canUsePepaiResearch = userEmail.toLowerCase().endsWith('@profitibull.com') || accessPepaiResearch;

  // Load credits on mount
  useEffect(() => {
    if (userEmail) {
      refreshContactCredits().catch(console.error);
      listSavedSearches(userEmail)
        .then((res) => {
          if (res.success) setSavedSearches(res.savedSearches);
        })
        .catch(console.error);
    }
  }, [refreshContactCredits, userEmail, setSavedSearches]);

  useEffect(() => {
    if (!userEmail) return;
    const timer = window.setInterval(() => {
      refreshContactCredits().catch(() => {
        // Ignore transient refresh failures.
      });
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [refreshContactCredits, userEmail]);

  useEffect(() => {
    if (!userEmail) return;
    fetch(`${API_CONFIG.RESEARCH_API}/get-lists?userEmail=${encodeURIComponent(userEmail)}&entityType=${LIST_ENTITY_TYPE}`)
      .then((res) => res.json())
      .then((data) => {
        if (!data?.success) return;
        const nextLists = data.lists || [];
        setUserLists(nextLists);
        if (nextLists.includes(defaultList)) {
          setCsvConfirmList((current) => current || defaultList);
          setActiveListName((current) => current || defaultList);
        }
      })
      .catch((e) => console.error('Failed to load lists', e));
  }, [defaultList, userEmail]);

  useEffect(() => {
    let active = true;
    if (!userEmail || !canChooseGhlTarget) {
      setGhlTargetOptions([]);
      setGhlSendAsLocationId('');
      return () => {
        active = false;
      };
    }
    if (!adminToken) {
      setGhlTargetOptions([]);
      return () => {
        active = false;
      };
    }
    (async () => {
      try {
        const settings = await getAccountSettings({
          token: adminToken,
          email: userEmail,
        });
        if (!active) return;
        const options = (settings.ghlSendAsOptions ?? [])
          .filter((opt) => opt.active !== false)
          .map((opt) => ({
            locationId: String(opt.locationId || '').trim(),
            label: `${firstNonEmptyString(opt.name, opt.locationId)} (${String(opt.locationId || '').trim()})`,
          }))
          .filter((opt) => Boolean(opt.locationId));
        setGhlTargetOptions(options);
        const savedLocationId = String(settings.account?.ghlSendAsLocationId || '').trim();
        setGhlSendAsLocationId(
          savedLocationId && options.some((option) => option.locationId === savedLocationId)
            ? savedLocationId
            : ''
        );
      } catch {
        if (!active) return;
        setGhlTargetOptions([]);
        setGhlSendAsLocationId('');
      }
    })();
    return () => {
      active = false;
    };
  }, [adminToken, canChooseGhlTarget, userEmail]);

  const persistDefaultList = useCallback((listName: string) => {
    setDefaultList(listName);
    safeStorageSet(DEFAULT_LIST_STORAGE_KEY, listName);
  }, []);

  const buildResearchTags = useCallback((listName?: string) => {
    const tags = new Set<string>();
    const trimmedListName = String(listName || '').trim();
    if (trimmedListName) tags.add(trimmedListName);
    if (canUsePepaiResearch && pepaiResearchEnabled) tags.add('PEPAI-research');
    return Array.from(tags);
  }, [canUsePepaiResearch, pepaiResearchEnabled]);

  const togglePepaiResearch = useCallback((enabled: boolean) => {
    const nextEnabled = canUsePepaiResearch ? enabled : false;
    setPepaiResearchEnabled(nextEnabled);
    safeStorageSet(PEPAI_RESEARCH_STORAGE_KEY, nextEnabled ? 'true' : 'false');
  }, [canUsePepaiResearch]);

  const toggleSuperScrape = useCallback((enabled: boolean) => {
    const nextEnabled = canUsePepaiResearch ? enabled : false;
    setSuperScrapeEnabled(nextEnabled);
    safeStorageSet(SUPER_SCRAPE_STORAGE_KEY, nextEnabled ? 'true' : 'false');
  }, [canUsePepaiResearch]);

  useEffect(() => {
    if (canUsePepaiResearch) return;
    setSuperScrapeEnabled(false);
    setPepaiResearchEnabled(false);
    safeStorageRemove(SUPER_SCRAPE_STORAGE_KEY);
    safeStorageRemove(PEPAI_RESEARCH_STORAGE_KEY);
    safeStorageRemove(PEPAI_CAMPAIGN_STORAGE_KEY);
  }, [canUsePepaiResearch]);

  useEffect(() => {
    if (!canUsePepaiResearch) {
      setInstantlyCampaigns([]);
      setSelectedInstantlyCampaignId('');
      return;
    }
    listInstantlyCampaignOptions({ userEmail })
      .then((data) => {
        if (!data?.success) return;
        const campaigns = Array.isArray(data.campaigns) ? data.campaigns : [];
        setInstantlyCampaigns(campaigns);
        setSelectedInstantlyCampaignId((current) => {
          const existing = current || safeStorageGet(PEPAI_CAMPAIGN_STORAGE_KEY) || '';
          if (existing && campaigns.some((campaign) => campaign.id === existing)) return existing;
          const defaultId =
            (data.defaultCampaign?.id && campaigns.some((campaign) => campaign.id === data.defaultCampaign?.id)
              ? data.defaultCampaign.id
              : campaigns[0]?.id) || '';
          if (defaultId) safeStorageSet(PEPAI_CAMPAIGN_STORAGE_KEY, defaultId);
          return defaultId;
        });
      })
      .catch((error) => console.error('Failed to load Instantly campaigns', error));
  }, [canUsePepaiResearch, userEmail]);

  const handleCreateList = useCallback(async () => {
    if (!userEmail) return;
    const trimmed = newListName.trim();
    if (!trimmed) {
      alert('List name required');
      return;
    }
    const response = await fetch(`${API_CONFIG.RESEARCH_API}/upsert-list`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userEmail,
        entityType: LIST_ENTITY_TYPE,
        listName: trimmed,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) throw new Error(data?.error || 'Failed to create list');
    const canonical = String(data.listName || '').trim();
    if (!canonical) throw new Error('Missing created list name');
    setUserLists((prev) => Array.from(new Set([...prev, canonical])).sort((a, b) => a.localeCompare(b)));
    setActiveListName(canonical);
    setCsvConfirmList(canonical);
    if (setNewListAsDefault) persistDefaultList(canonical);
    setCreateListModalOpen(false);
    setNewListName('');
    setSetNewListAsDefault(true);
  }, [newListName, persistDefaultList, setNewListAsDefault, userEmail]);

  const handleAddSelectedToList = useCallback(async () => {
    if (!userEmail) return;
    if (!activeListName) {
      alert('Choose a contact list first');
      return;
    }
    const searchResultIds = getSelectedIndices()
      .map((index) => leads[index]?.searchResultId)
      .filter((value): value is string => Boolean(value));
    if (searchResultIds.length === 0) {
      alert('Select at least one contact');
      return;
    }
    const response = await fetch(`${API_CONFIG.RESEARCH_API}/update-lists`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userEmail,
        entityType: LIST_ENTITY_TYPE,
        action: 'add',
        listName: activeListName,
        searchResultIds,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) throw new Error(data?.error || 'Failed to add contacts to list');
    alert(`Added ${searchResultIds.length} contacts to ${activeListName}`);
  }, [activeListName, getSelectedIndices, leads, userEmail]);

  // Search handler
  const handleSearch = useCallback(async (nextFilters?: SearchFiltersType) => {
    const activeFilters = nextFilters ?? filters;
    setLoading(true);
    addRecentSearch(activeFilters);
    setActiveSavedSearchId(null);
    setCurrentPage(0);

    try {
      const cleanFilters = { ...activeFilters, contactSearchId: null, nextToken: null };
      const result = await searchLeads(cleanFilters, 0, perPage, { userEmail });
      setLeads(result.contacts, result.total);
      setFilters({ contactSearchId: result.contactSearchId, nextToken: result.nextToken });
    } catch (e) {
      console.error(e);
      alert('Search failed');
    } finally {
      setLoading(false);
    }
  }, [filters, perPage, setLoading, setLeads, setCurrentPage, addRecentSearch, setActiveSavedSearchId, setFilters, userEmail]);

  useEffect(() => {
    if (!pendingSearch) return;
    const run = async () => {
      setLoading(true);
      try {
        const result = await searchLeads(pendingSearch.filters, pendingSearch.page, pendingSearch.perPage, { userEmail });
        setLeads(result.contacts, result.total);
        setFilters({ contactSearchId: result.contactSearchId, nextToken: result.nextToken });
        setCurrentPage(pendingSearch.page);
        if (activeSavedSearchId && userEmail) {
          await updateSavedSearchPage({ userEmail, savedSearchId: activeSavedSearchId, lastPage: pendingSearch.page, perPage: pendingSearch.perPage });
          updateSavedSearchPageLocal(activeSavedSearchId, pendingSearch.page, pendingSearch.perPage);
        }
      } catch (e) {
        console.error(e);
        alert('Search failed');
      } finally {
        setLoading(false);
        setPendingSearch(null);
      }
    };
    run();
  }, [pendingSearch, setLeads, setCurrentPage, setLoading, setPendingSearch, activeSavedSearchId, userEmail, updateSavedSearchPageLocal, setFilters]);

  useEffect(() => {
    if (!activeSavedSearchId || !userEmail) return;
    updateSavedSearchPage({ userEmail, savedSearchId: activeSavedSearchId, lastPage: currentPage, perPage })
      .then(() => updateSavedSearchPageLocal(activeSavedSearchId, currentPage, perPage))
      .catch(console.error);
  }, [activeSavedSearchId, currentPage, perPage, userEmail, updateSavedSearchPageLocal]);

  // Pagination
  const handlePrevPage = useCallback(async () => {
    if (currentPage === 0) return;
    setLoading(true);
    try {
      const result = await searchLeads(filters, currentPage - 1, perPage, { userEmail });
      setLeads(result.contacts, result.total);
      setFilters({ contactSearchId: result.contactSearchId, nextToken: result.nextToken });
      setCurrentPage(currentPage - 1);
      if (activeSavedSearchId && userEmail) {
        await updateSavedSearchPage({ userEmail, savedSearchId: activeSavedSearchId, lastPage: currentPage - 1, perPage });
        updateSavedSearchPageLocal(activeSavedSearchId, currentPage - 1, perPage);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, perPage, setLeads, setCurrentPage, setLoading, activeSavedSearchId, userEmail, updateSavedSearchPageLocal, setFilters]);

  const handleNextPage = useCallback(async () => {
    const totalPages = Math.ceil(totalResults / perPage);
    if (currentPage >= totalPages - 1) return;
    setLoading(true);
    try {
      const result = await searchLeads(filters, currentPage + 1, perPage, { userEmail });
      setLeads(result.contacts, result.total);
      setFilters({ contactSearchId: result.contactSearchId, nextToken: result.nextToken });
      setCurrentPage(currentPage + 1);
      if (activeSavedSearchId && userEmail) {
        await updateSavedSearchPage({ userEmail, savedSearchId: activeSavedSearchId, lastPage: currentPage + 1, perPage });
        updateSavedSearchPageLocal(activeSavedSearchId, currentPage + 1, perPage);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [currentPage, totalResults, filters, perPage, setLeads, setCurrentPage, setLoading, activeSavedSearchId, userEmail, updateSavedSearchPageLocal, setFilters]);

  const startResearchJob = useCallback((label: string, total: number) => {
    const id = `research-${label.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}`;
    addScrapeJob({
      id,
      label,
      source: 'research',
      companyName: '',
      queuedAt: new Date().toISOString(),
      status: 'queued',
      progress: 5,
      message: `Starting 0/${total}`,
    });
    updateScrapeJob(id, { status: 'running', startedAt: new Date().toISOString() });
    return id;
  }, [addScrapeJob, updateScrapeJob]);

  const consumeResearchCredit = useCallback(async () => {
    if (FEATURE_FLAGS.SERVER_SIDE_CREDITS) return;
    if (!userEmail) throw new Error('Missing user');
    await deductCredits(userEmail, RESEARCH_CREDIT_COST, 'research_contact', 'contact');
    await refreshContactCredits();
  }, [refreshContactCredits, userEmail]);

  const getLeadDisplayName = useCallback((lead: any) => {
    return firstNonEmptyString(
      lead?.name,
      `${lead?.firstName || ''} ${lead?.lastName || ''}`.trim(),
      lead?.company,
      lead?.title,
      'Contact'
    );
  }, []);

  const updateResearchJobStep = useCallback((
    jobId: string,
    index: number,
    total: number,
    displayName: string,
    stepLabel: string,
    stepOffset: number
  ) => {
    const baseCompleted = Math.max(0, index - 1);
    const overall = (baseCompleted + stepOffset) / Math.max(total, 1);
    const progress = Math.min(99, Math.max(5, Math.round(overall * 100)));
    updateScrapeJob(jobId, {
      progress,
      message: `Processing ${index}/${total} · ${displayName} · ${stepLabel}`,
    });
  }, [updateScrapeJob]);

  const processResearchLead = useCallback(async (
    lead: any,
    options: {
      jobId: string;
      index: number;
      total: number;
      listName?: string;
      addToListAfterResearch?: boolean;
    }
  ) => {
    const { jobId, index, total, listName, addToListAfterResearch } = options;
    const displayName = getLeadDisplayName(lead);
    const tags = buildResearchTags(listName);
    const selectedCampaign =
      pepaiResearchEnabled && selectedInstantlyCampaignId
        ? instantlyCampaigns.find((campaign) => campaign.id === selectedInstantlyCampaignId) ?? null
        : null;

    updateResearchJobStep(jobId, index, total, displayName, superScrapeEnabled ? '1a Seamless' : '1a Research', 0.1);
    await consumeResearchCredit();
    let result: ResearchPipelineResult = await researchContact(lead, userEmail, {
      extraTags: tags,
      superScrape: superScrapeEnabled,
      pepaiResearch: pepaiResearchEnabled,
      instantlyCampaignId: selectedCampaign?.id,
      instantlyCampaignName: selectedCampaign?.name,
      targetGhlLocationId,
    });

    if (!result.success || !result.data) {
      const message = String((result as { error?: string }).error || 'Research failed');
      if (/insufficient contact credits/i.test(message)) {
        const latest = await refreshContactCredits().catch(() => null);
        if (typeof latest === 'number' && latest > 0) {
          // Transient desync between research service and credit display: retry once.
          result = await researchContact(lead, userEmail, {
            extraTags: tags,
            superScrape: superScrapeEnabled,
            pepaiResearch: pepaiResearchEnabled,
            instantlyCampaignId: selectedCampaign?.id,
            instantlyCampaignName: selectedCampaign?.name,
            targetGhlLocationId,
          });
          if (result.success && result.data) {
            // continue with retried result
          } else {
            const retryMessage = String((result as { error?: string }).error || message);
            throw new Error(retryMessage);
          }
        } else if (latest != null) {
          throw new Error(`Insufficient contact credits (${latest} remaining).`);
        }
      }
      if (!result.success || !result.data) throw new Error(message);
    }
    let finalResult: ResearchPipelineResult = result;
    const asyncStatus = String((result as { async?: { status?: string } }).async?.status || '').toLowerCase();
    if (superScrapeEnabled && asyncStatus === 'queued' && result.data?.contactId) {
      updateScrapeJob(jobId, {
        status: 'running',
        progress: 20,
        message: pepaiResearchEnabled
          ? 'Queued · 1b Apollo · 1c Crunchbase · 1d Prospect Assignment · 1e CRM Delivery · 1f Email / Instantly Campaign Delivery'
          : 'Queued · 1b Apollo · 1c Crunchbase · 1d Prospect Assignment · 1e CRM Delivery',
      });
      let latestStatus: Awaited<ReturnType<typeof getResearchStatus>> | null = null;
      for (let attempt = 0; attempt < 300; attempt += 1) {
        await sleep(2000);
        latestStatus = await getResearchStatus(userEmail, String(result.data.contactId));
        if (latestStatus.stageLabel) {
          updateScrapeJob(jobId, {
            status: 'running',
            progress: Math.min(99, Math.max(20, Math.round(latestStatus.progress || 20))),
            message: `Processing ${index}/${total} · ${displayName} · ${latestStatus.stageLabel}`,
          });
        }
        if (latestStatus.data) {
          finalResult = {
            ...result,
            data: latestStatus.data,
            instantly: (latestStatus.data as Record<string, unknown>).instantlySequence ?? (result as Record<string, unknown>).instantly,
          } as ResearchPipelineResult;
        }
        if (latestStatus.done) {
          if (latestStatus.status === 'failed' || latestStatus.status === 'failed_timeout') {
            throw new Error(latestStatus.error || 'Super Scrape failed');
          }
          break;
        }
      }
      if (!latestStatus?.done) {
        throw new Error('Super Scrape status polling timed out');
      }
    }

    saveResearchedContact(lead.searchResultId, finalResult.data);

    if (addToListAfterResearch && listName) {
      const resolvedSearchResultId = String(
        (finalResult.data as Record<string, unknown>)?.searchResultId ??
        (finalResult.data as Record<string, unknown>)?.search_result_id ??
        (finalResult.data as Record<string, unknown>)?.sourceSearchResultId ??
        lead?.searchResultId ??
        ''
      ).trim();
      const resolvedContactId = String(
        (finalResult.data as Record<string, unknown>)?.contactId ??
        (finalResult.data as Record<string, unknown>)?.id ??
        ((finalResult as unknown as Record<string, unknown>)?.meta as Record<string, unknown> | undefined)?.contactId ??
        ''
      ).trim();
      if (!resolvedSearchResultId && !resolvedContactId) {
        console.error('Skipped list add after research: missing identifiers', { listName });
      } else {
        try {
          const attachPayloads = [
            {
              searchResultIds: resolvedSearchResultId ? [resolvedSearchResultId] : [],
              contactIds: resolvedContactId ? [resolvedContactId] : [],
            },
            {
              searchResultIds: [],
              contactIds: resolvedContactId ? [resolvedContactId] : [],
            },
            {
              searchResultIds: resolvedSearchResultId ? [resolvedSearchResultId] : [],
              contactIds: [],
            },
          ].filter((candidate) => candidate.searchResultIds.length > 0 || candidate.contactIds.length > 0);

          let attached = false;
          for (const payloadCandidate of attachPayloads) {
            const listRes = await fetch(`${API_CONFIG.RESEARCH_API}/update-lists`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                userEmail,
                entityType: LIST_ENTITY_TYPE,
                action: 'add',
                listName,
                searchResultIds: payloadCandidate.searchResultIds,
                contactIds: payloadCandidate.contactIds,
              }),
            });
            const listData = await listRes.json().catch(() => ({})) as {
              success?: boolean;
              updated?: number;
              memberCount?: number;
              error?: string;
            };
            const updated = Number(listData?.updated ?? 0);
            const memberCount = Number(listData?.memberCount ?? 0);
            if (listRes.ok && listData?.success && (updated > 0 || memberCount > 0)) {
              attached = true;
              break;
            }
          }

          if (!attached) {
            console.error('Failed to add researched contact to list', {
              listName,
              searchResultId: resolvedSearchResultId,
              contactId: resolvedContactId,
              reason: 'update_lists_no_match',
            });
          }
        } catch (e) {
          console.error('Failed to add researched contact to list', {
            listName,
            searchResultId: resolvedSearchResultId,
            contactId: resolvedContactId,
            error: e instanceof Error ? e.message : String(e),
          });
        }
      }
    }

    if (superScrapeEnabled) {
      updateScrapeJob(jobId, {
        status: 'running',
        progress: 95,
        message: `Processing ${index}/${total} · ${displayName} · Finalizing`,
      });
    } else {
      updateResearchJobStep(jobId, index, total, displayName, '1b Prospect Assignment', 0.45);
    }
    if (!superScrapeEnabled && String(finalResult.prospectAssignment?.status || '').toUpperCase() === 'FAILED') {
      throw new Error(finalResult.prospectAssignment?.reason || 'Prospect assignment failed');
    }

    if (superScrapeEnabled) {
      // Async super scrape persists its own stage-by-stage progress in Dynamo; do not validate
      // against the initial queued response object here.
    } else {
      const stepCLabel = '1c CRM Sync';
      updateResearchJobStep(jobId, index, total, displayName, stepCLabel, 0.8);
      if (String(finalResult.crmSync?.status || '').toUpperCase() === 'FAILED') {
        throw new Error(finalResult.crmSync?.reason || 'CRM sync failed');
      }
    }
    if (pepaiResearchEnabled) {
      updateResearchJobStep(
        jobId,
        index,
        total,
        displayName,
        superScrapeEnabled ? '1e Email / Instantly Campaign Delivery' : '1d Email / Instantly Campaign Delivery',
        0.92
      );
      const instantlyStatus = String(finalResult.instantly?.status || '').toLowerCase();
      const isDeferredInstantly = instantlyStatus === 'skipped';
      if (!isDeferredInstantly && instantlyStatus !== 'queued' && instantlyStatus !== 'polling' && instantlyStatus !== 'completed') {
        throw new Error(finalResult.instantly?.error || `Instantly enrollment ${instantlyStatus || 'did not run'}`);
      }
    }

    updateScrapeJob(jobId, {
      progress: Math.min(99, Math.max(5, Math.round((index / Math.max(total, 1)) * 100))),
      message: `Completed ${index}/${total} · ${displayName}`,
    });

    return finalResult;
  }, [
    buildResearchTags,
    consumeResearchCredit,
    getLeadDisplayName,
    targetGhlLocationId,
    isAdmin,
    instantlyCampaigns,
    pepaiResearchEnabled,
    refreshContactCredits,
    saveResearchedContact,
    selectedInstantlyCampaignId,
    superScrapeEnabled,
    updateResearchJobStep,
    updateScrapeJob,
    userEmail,
  ]);

  const runResearchQueueForLeads = useCallback((leadList: any[], label: string) => {
    const total = leadList.length;
    const jobId = startResearchJob(label, total);
    const targetListName = activeListName || defaultList;
    let saved = 0;
    let done = 0;

    Promise.resolve().then(async () => {
      try {
        await refreshContactCredits();
        for (const [leadIndex, lead] of leadList.entries()) {
          if (!lead) continue;
          const result = await processResearchLead(lead, {
            jobId,
            index: leadIndex + 1,
            total,
            listName: targetListName,
            addToListAfterResearch: Boolean(targetListName),
          });
          if (result.success && result.data) saved += 1;
          done += 1;
        }
        updateScrapeJob(jobId, { status: 'succeeded', progress: 100, message: `Complete (${saved}/${total})`, finishedAt: new Date().toISOString() });
        alert(`Researched ${saved} of ${total} contacts`);
      } catch (e) {
        console.error(e);
        updateScrapeJob(jobId, { status: 'failed', progress: 100, message: e instanceof Error ? e.message : 'Failed', finishedAt: new Date().toISOString() });
        alert(e instanceof Error ? e.message : 'Research failed or insufficient credits');
      }
    });
  }, [activeListName, defaultList, processResearchLead, refreshContactCredits, startResearchJob, updateScrapeJob]);

  const runResearchQueue = useCallback((indices: number[], label: string) => {
    const leadList = indices.map((i) => leads[i]).filter(Boolean);
    runResearchQueueForLeads(leadList, label);
  }, [leads, runResearchQueueForLeads]);

  // Research selected
  const handleResearchSelected = useCallback(async () => {
    const indices = getSelectedIndices();
    const unresearchedIndices = indices.filter(
      (i) => !getResearchedContactForLead(leads[i])
    );

    if (unresearchedIndices.length === 0) {
      alert('All selected contacts already researched');
      return;
    }

    runResearchQueue(unresearchedIndices, 'Research Selected');
  }, [getSelectedIndices, leads, getResearchedContactForLead, runResearchQueue]);

  const handleReResearchSelected = useCallback(async () => {
    const indices = getSelectedIndices();
    const targetListName = activeListName || defaultList;
    if (indices.length === 0) {
      alert('Select at least one contact');
      return;
    }
    if (!confirm(`Re-run research for ${indices.length} contacts? This will use credits.`)) {
      return;
    }
    setLoading(true);
    let jobId = '';
    try {
      await refreshContactCredits();
      const total = indices.length;
      jobId = startResearchJob('Re-Run Selected', total);
      let completed = 0;
      for (const [order, i] of indices.entries()) {
        const lead = leads[i];
        if (!lead) continue;
        const result = await processResearchLead(lead, {
          jobId,
          index: order + 1,
          total,
          listName: targetListName,
          addToListAfterResearch: Boolean(targetListName),
        });
        if (result.success && result.data) completed += 1;
      }
      updateScrapeJob(jobId, { status: 'succeeded', progress: 100, message: `Complete (${completed}/${total})`, finishedAt: new Date().toISOString() });
      alert('Re-run research complete');
    } catch (e) {
      console.error(e);
      if (jobId) {
        updateScrapeJob(jobId, {
          status: 'failed',
          progress: 100,
          message: e instanceof Error ? e.message : 'Failed',
          finishedAt: new Date().toISOString(),
        });
      }
      alert(e instanceof Error ? e.message : 'Re-run research failed or insufficient credits');
    } finally {
      setLoading(false);
    }
  }, [activeListName, defaultList, getSelectedIndices, leads, processResearchLead, refreshContactCredits, setLoading, startResearchJob, updateScrapeJob]);

  // Research all
  const handleResearchAll = useCallback(async () => {
    const unresearchedIndices = leads
      .map((l, i) => ({ lead: l, idx: i }))
      .filter((x) => !getResearchedContactForLead(x.lead))
      .map((x) => x.idx);

    if (unresearchedIndices.length === 0) {
      alert('All contacts already researched');
      return;
    }

    if (!confirm(`Research all ${unresearchedIndices.length} contacts? This will use credits.`)) {
      return;
    }

    runResearchQueue(unresearchedIndices, 'Research All');
  }, [leads, getResearchedContactForLead, runResearchQueue]);

  // Export
  const handleExport = useCallback(() => {
    if (leads.length === 0) {
      alert('No results to export');
      return;
    }
    exportLeadsToCSV(leads, researchedContacts);
  }, [leads, researchedContacts]);

  // Refresh results after research
  const handleResearchComplete = useCallback(() => {
    // Just triggers a re-render
  }, []);

  const handleSingleResearch = useCallback(async (lead: any) => {
    const jobId = startResearchJob(superScrapeEnabled ? 'Super Scrape' : 'Get Data', 1);
    const targetListName = activeListName || defaultList;
    try {
      await refreshContactCredits();
      await processResearchLead(lead, {
        jobId,
        index: 1,
        total: 1,
        listName: targetListName,
        addToListAfterResearch: Boolean(targetListName),
      });
      updateScrapeJob(jobId, {
        status: 'succeeded',
        progress: 100,
        message: 'Complete (1/1)',
        finishedAt: new Date().toISOString(),
      });
    } catch (e) {
      updateScrapeJob(jobId, {
        status: 'failed',
        progress: 100,
        message: e instanceof Error ? e.message : 'Failed',
        finishedAt: new Date().toISOString(),
      });
      throw e;
    }
  }, [activeListName, defaultList, pepaiResearchEnabled, processResearchLead, refreshContactCredits, startResearchJob, superScrapeEnabled, updateScrapeJob]);

  const totalPages = Math.ceil(totalResults / perPage) || 1;

  const splitCsvLine = (line: string): string[] => {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        const next = line[i + 1];
        if (inQuotes && next === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        out.push(cur);
        cur = '';
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map((v) => v.trim());
  };

  const normalizeKeyword = (value: string): string =>
    value
      .trim()
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .replace(/\/+$/, '');

  const toHeaderSlug = (value: string) =>
    value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  const findHeaderMatch = (headers: string[], candidates: string[]) => {
    const lowered = headers.map((h) => toHeaderSlug(h));
    const idx = lowered.findIndex((h) => candidates.includes(h));
    return idx >= 0 ? headers[idx] : '';
  };

  const csvPresetStorageKey = userEmail ? `research_csv_mapping_${userEmail}` : 'research_csv_mapping_default';

  const detectCsvMapping = (headers: string[]): CsvMapping => {
    const auto: CsvMapping = {
      mode: 'structured',
      nameColumn: findHeaderMatch(headers, ['name', 'full name', 'contact name', 'contact_name', 'fullname']),
      companyColumn: findHeaderMatch(headers, ['company', 'company name', 'company_name', 'companyname']),
      industryColumn: findHeaderMatch(headers, ['industry', 'company industry', 'company_industry']),
      websiteColumn: findHeaderMatch(headers, ['website', 'url', 'domain', 'company website', 'company_website', 'company url', 'company_url']),
      keywordColumn: findHeaderMatch(headers, ['keyword', 'keywords', 'linkedin', 'linkedin url', 'linkedin_url', 'url', 'website']),
    };
    try {
      const raw = safeStorageGet(csvPresetStorageKey);
      if (!raw) return auto;
      const preset = JSON.parse(raw) as CsvMapping;
      const safe = (value: string) => (value === '__any__' || headers.includes(value) ? value : '');
      const mode: CsvMappingMode = preset?.mode === 'keyword' ? 'keyword' : 'structured';
      return {
        mode,
        nameColumn: safe(preset?.nameColumn || auto.nameColumn),
        companyColumn: safe(preset?.companyColumn || auto.companyColumn),
        industryColumn: safe(preset?.industryColumn || auto.industryColumn),
        websiteColumn: safe(preset?.websiteColumn || auto.websiteColumn),
        keywordColumn: safe(preset?.keywordColumn || auto.keywordColumn),
      };
    } catch {
      return auto;
    }
  };

  const normalize = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

  const withTimeout = async <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
    let timeoutId: number | undefined;
    const timeoutPromise = new Promise<T>((_, reject) => {
      timeoutId = window.setTimeout(() => reject(new Error(`${label} timeout`)), ms);
    });
    try {
      return await Promise.race([promise, timeoutPromise]);
    } finally {
      if (timeoutId) window.clearTimeout(timeoutId);
    }
  };

  const runCsvRowByRow = useCallback(
    async (rows: CsvImportRow[], jobId: string, targetList?: string) => {
      const total = Math.max(rows.length, 1);
      let processed = 0;
      let researched = 0;
      let consecutiveErrors = 0;

      for (const row of rows) {
        const rowName = row.name?.trim();
        const rowCompany = row.company?.trim();
        if (!rowCompany && !row.website && row.keywords.length === 0) {
          processed += 1;
          updateScrapeJob(jobId, {
            progress: Math.min(95, Math.max(5, Math.round((processed / total) * 100))),
            message: `Row ${processed}/${total} · Skipped (missing company/keyword)`,
          });
          continue;
        }

        processed += 1;
        const company = row.company;
        const industry = row.industry;
        const rowKeywords = row.keywords.length > 0
          ? row.keywords
          : (row.website ? [row.website] : (industry ? [industry] : []));
        setCsvImportStatus(`Running search... (${processed}/${total})`);

        let result: any;
        try {
          result = await withTimeout(
            searchLeads(
              {
                ...filters,
                names: [],
                titles: [],
                companies: company ? [company] : [],
                industries: [],
                keywords: rowKeywords,
              },
              0,
              perPage,
              { userEmail }
            ),
            20000,
            'search'
          );
        } catch (e) {
          console.error(e);
          consecutiveErrors += 1;
          updateScrapeJob(jobId, {
            progress: Math.min(95, Math.max(5, Math.round((processed / total) * 100))),
            message: `Row ${processed}/${total} · ${rowCompany} · Search failed`,
          });
          if (consecutiveErrors >= 5) {
            await sleep(5000);
            consecutiveErrors = 0;
          } else {
            await sleep(500);
          }
          continue;
        }
        consecutiveErrors = 0;

        const leadsList = result.contacts ?? [];
        setLeads(leadsList, result.total ?? leadsList.length);
        setCurrentPage(0);

        const targetName = normalize(rowName || '');
        const targetCompany = normalize(rowCompany || '');
        const companyMatches = leadsList.filter((lead: any) => {
          const leadCompany = normalize(lead.company || lead.companyName || lead.Company || '');
          if (targetCompany) {
            return leadCompany === targetCompany;
          }
          return true;
        });

        const prioritized = targetName
          ? [
              ...companyMatches.filter((lead: any) => {
                const leadName =
                  normalize(lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`.trim());
                return leadName === targetName;
              }),
              ...companyMatches.filter((lead: any) => {
                const leadName =
                  normalize(lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`.trim());
                return leadName !== targetName;
              }),
            ]
          : companyMatches;

        const rowTargets = prioritized
          .filter((lead: any) => lead?.searchResultId && !getResearchedContactForLead(lead))
          .slice(0, 5);

        for (const target of rowTargets) {
          try {
            const listName = targetList || defaultList || activeListName;
            const research = await withTimeout(
              processResearchLead(target, {
                jobId,
                index: processed,
                total,
                listName,
                addToListAfterResearch: Boolean(listName),
              }),
              45000,
              'research'
            );
            if (research.success && research.data) {
              researched += 1;
            }
          } catch (e) {
            console.error(e);
          }
        }

        const progress = Math.min(95, Math.max(5, Math.round((processed / total) * 100)));
        updateScrapeJob(jobId, {
          progress,
          message: `Row ${processed}/${total} · ${rowCompany} · Researched ${researched}`,
        });

        if (processed % 25 === 0) {
          await sleep(1000);
        } else {
          await sleep(200);
        }
      }
    },
    [perPage, filters, getResearchedContactForLead, userEmail, setLeads, setCurrentPage, updateScrapeJob, normalize, defaultList, activeListName, processResearchLead]
  );

  const applyCsvMapping = useCallback((rawData: CsvRawData, mapping: CsvMapping) => {
    const indexByHeader = new Map<string, number>(rawData.headers.map((h, idx) => [h, idx]));
    const readCell = (cols: string[], header: string) => {
      if (!header) return '';
      const idx = indexByHeader.get(header);
      if (idx === undefined) return '';
      return String(cols[idx] ?? '').trim();
    };
    const readKeywords = (cols: string[]) => {
      if (mapping.mode === 'keyword') {
        if (mapping.keywordColumn === '__any__') {
          return cols.map((v) => normalizeKeyword(String(v ?? ''))).filter(Boolean);
        }
        const one = normalizeKeyword(readCell(cols, mapping.keywordColumn));
        return one ? [one] : [];
      }
      const kw = normalizeKeyword(readCell(cols, mapping.keywordColumn));
      const web = normalizeKeyword(readCell(cols, mapping.websiteColumn));
      return Array.from(new Set([kw, web].filter(Boolean)));
    };

    const mapped: CsvImportRow[] = rawData.rows.map((cols) => {
      const name = readCell(cols, mapping.nameColumn);
      const company = readCell(cols, mapping.companyColumn);
      const industry = readCell(cols, mapping.industryColumn);
      const website = normalizeKeyword(readCell(cols, mapping.websiteColumn));
      const keywords = readKeywords(cols);
      return { name, company, industry, website, keywords };
    });

    const validRows = mapped.filter((r) => r.company || r.website || r.keywords.length > 0);
    return { mapped, validRows };
  }, []);

  const handleImportCsv = async (file: File) => {
    setCsvStatus(null);
    setCsvImportStatus(null);
    setCsvImportCount(null);
    setCsvImportResults(null);
    setCsvConfirmOpen(false);
    setCsvMapOpen(false);
    setCsvConfirmCount(0);
    setCsvPendingRows([]);
    setCsvConfirmList(activeListName || defaultList);
    setCsvRawData(null);
    const text = await file.text();
    const lines = text.replace(/\r\n/g, '\n').split('\n').filter((l) => l.trim().length);
    if (lines.length < 2) {
      setCsvStatus('CSV has no data rows.');
      return;
    }
    const headers = splitCsvLine(lines[0]).map((h) => h.trim()).filter(Boolean);
    const rows = lines.slice(1).map((line) => splitCsvLine(line));
    const rawData: CsvRawData = { headers, rows };
    setCsvRawData(rawData);
    setCsvMapping(detectCsvMapping(headers));
    setCsvMapOpen(true);
  };
  const unresearchedCount = leads.filter((l) => !getResearchedContactForLead(l)).length;
  const selectedCount = selectedLeads.size;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Please Log In</h1>
          <a href="/login" className="text-red-500 hover:text-red-400">
            Go to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/75 z-40 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-red-500 mx-auto mb-4" />
            <p className="text-white text-lg">Searching...</p>
          </div>
        </div>
      )}

      {csvMapOpen && csvRawData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-card-dark border border-border-dark rounded-xl p-6 w-full max-w-2xl">
            <h3 className="text-lg font-semibold text-white mb-2">Map CSV Columns</h3>
            <p className="text-sm text-gray-300 mb-4">
              Use any header names. We support keyword-only and structured imports.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <label className="block">
                <div className="text-xs text-gray-400 mb-1">Import mode</div>
                <select
                  value={csvMapping.mode}
                  onChange={(e) => setCsvMapping((prev) => ({ ...prev, mode: e.target.value as CsvMappingMode }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white"
                >
                  <option value="structured">Structured mode</option>
                  <option value="keyword">Keyword mode</option>
                </select>
              </label>
              <label className="block">
                <div className="text-xs text-gray-400 mb-1">Keyword column</div>
                <select
                  value={csvMapping.keywordColumn}
                  onChange={(e) => setCsvMapping((prev) => ({ ...prev, keywordColumn: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white"
                >
                  <option value="">(none)</option>
                  <option value="__any__">Any column (concatenate row)</option>
                  {csvRawData.headers.map((h) => <option key={`kw-${h}`} value={h}>{h}</option>)}
                </select>
              </label>
              <label className="block">
                <div className="text-xs text-gray-400 mb-1">Name column</div>
                <select
                  value={csvMapping.nameColumn}
                  onChange={(e) => setCsvMapping((prev) => ({ ...prev, nameColumn: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white"
                >
                  <option value="">(none)</option>
                  {csvRawData.headers.map((h) => <option key={`nm-${h}`} value={h}>{h}</option>)}
                </select>
              </label>
              <label className="block">
                <div className="text-xs text-gray-400 mb-1">Company column</div>
                <select
                  value={csvMapping.companyColumn}
                  onChange={(e) => setCsvMapping((prev) => ({ ...prev, companyColumn: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white"
                >
                  <option value="">(none)</option>
                  {csvRawData.headers.map((h) => <option key={`co-${h}`} value={h}>{h}</option>)}
                </select>
              </label>
              <label className="block">
                <div className="text-xs text-gray-400 mb-1">Title/Industry column</div>
                <select
                  value={csvMapping.industryColumn}
                  onChange={(e) => setCsvMapping((prev) => ({ ...prev, industryColumn: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white"
                >
                  <option value="">(none)</option>
                  {csvRawData.headers.map((h) => <option key={`in-${h}`} value={h}>{h}</option>)}
                </select>
              </label>
              <label className="block">
                <div className="text-xs text-gray-400 mb-1">Website/URL column</div>
                <select
                  value={csvMapping.websiteColumn}
                  onChange={(e) => setCsvMapping((prev) => ({ ...prev, websiteColumn: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white"
                >
                  <option value="">(none)</option>
                  {csvRawData.headers.map((h) => <option key={`we-${h}`} value={h}>{h}</option>)}
                </select>
              </label>
            </div>
            <p className="text-xs text-amber-300 mb-4">
              Recommended headers are optional. Skill names/keywords are matched verbatim and can be error-prone with typos.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white text-sm"
                onClick={() => {
                  setCsvMapOpen(false);
                  setCsvRawData(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm"
                onClick={() => {
                  if (csvMapping.mode === 'keyword' && !csvMapping.keywordColumn) {
                    setCsvStatus('Pick a keyword column for keyword mode.');
                    return;
                  }
                  const { mapped, validRows } = applyCsvMapping(csvRawData, csvMapping);
                  setCsvMapOpen(false);
                  setCsvImportCount(mapped.length);
                  if (!validRows.length) {
                    setCsvStatus('No valid rows after mapping. Map company/url/keyword fields.');
                    setCsvImportStatus(null);
                    return;
                  }
                  safeStorageSet(csvPresetStorageKey, JSON.stringify(csvMapping));
                  setCsvPendingRows(validRows);
                  setCsvStatus(`Imported ${mapped.length} rows.`);
                  setCsvImportStatus('Ready to confirm.');
                  setCsvConfirmCount(validRows.length);
                  setCsvConfirmOpen(true);
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {csvConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-card-dark border border-border-dark rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-2">Imported List Detected</h3>
            <p className="text-sm text-gray-300 mb-4">
              {csvConfirmCount} Rows, estimated credits: <span className="text-white font-semibold">{csvConfirmCount}</span>.
              Please press continue to confirm.
            </p>
            <div className="mb-4">
              <label className="block text-xs text-gray-400 mb-1">Add results to list</label>
              <select
                value={csvConfirmList}
                onChange={(e) => setCsvConfirmList(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white"
              >
                <option value="">No list</option>
                {userLists.map((list) => (
                  <option key={list} value={list}>
                    {list}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white text-sm"
                onClick={() => {
                  setCsvConfirmOpen(false);
                  setCsvConfirmCount(0);
                  setCsvPendingRows([]);
                  setCsvConfirmList(activeListName || defaultList);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm"
                onClick={async () => {
                  const rows = csvPendingRows;
                  setCsvConfirmOpen(false);
                  if (!rows.length) return;
                  const jobId = startResearchJob('CSV Import Search', rows.length);
                  updateScrapeJob(jobId, { status: 'running', progress: 5, message: 'Starting CSV search…' });
                  Promise.resolve().then(async () => {
                    try {
                      await runCsvRowByRow(rows, jobId, csvConfirmList || undefined);
                      setCsvImportStatus('Search + research complete.');
                      setCsvImportResults(rows.length);
                      updateScrapeJob(jobId, { status: 'succeeded', progress: 100, message: 'Complete', finishedAt: new Date().toISOString() });
                    } catch (e) {
                      console.error(e);
                      setCsvImportStatus('Search failed.');
                      updateScrapeJob(jobId, { status: 'failed', progress: 100, message: 'Failed', finishedAt: new Date().toISOString() });
                    } finally {
                      setCsvPendingRows([]);
                      setCsvConfirmList(activeListName || defaultList);
                    }
                  });
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {createListModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-card-dark border border-border-dark rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-2">Create Contact List</h3>
            <p className="text-sm text-gray-300 mb-4">Lists created here are available in Contact Search and My Lists.</p>
            <input
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              placeholder="VIP Prospects"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white mb-3"
            />
            <label className="inline-flex items-center gap-2 text-sm text-gray-300 mb-4">
              <input
                type="checkbox"
                checked={setNewListAsDefault}
                onChange={(e) => setSetNewListAsDefault(e.target.checked)}
              />
              Set as default list for research
            </label>
            <div className="flex items-center justify-end gap-2">
              <button
                className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white text-sm"
                onClick={() => {
                  setCreateListModalOpen(false);
                  setNewListName('');
                  setSetNewListAsDefault(true);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm"
                onClick={() => {
                  handleCreateList().catch((e) => {
                    console.error(e);
                    alert(e instanceof Error ? e.message : 'Failed to create list');
                  });
                }}
              >
                Create List
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="px-4 pt-4">
        <SearchFilters onSearch={handleSearch} />
      </div>

      <div className="p-4">
          {/* Results Header */}
          <div className="flex justify-between items-center gap-4 mb-4">
            <div className="text-sm text-gray-400">
              Showing <span className="text-white font-bold">{leads.length}</span> results{' '}
              <span className="text-gray-500">({totalResults.toLocaleString()} total)</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <select
                  value={activeListName}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setActiveListName(nextValue);
                    setCsvConfirmList(nextValue);
                    persistDefaultList(nextValue);
                  }}
                  className="px-3 py-1 bg-gray-800 border border-gray-700 rounded text-sm text-white min-w-[220px]"
                >
                  <option value="">No contact list</option>
                  {userLists.map((list) => (
                    <option key={list} value={list}>
                      {list}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setCreateListModalOpen(true)}
                  className="bg-gray-800 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Create List
                </button>
                <button
                  onClick={() => {
                    handleAddSelectedToList().catch((e) => {
                      console.error(e);
                      alert(e instanceof Error ? e.message : 'Failed to update list');
                    });
                  }}
                  disabled={selectedCount === 0 || !activeListName}
                  className="bg-indigo-700 hover:bg-indigo-600 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
                >
                  Add Selected To List ({selectedCount})
                </button>
              </div>
              {canUsePepaiResearch && (
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 rounded border border-gray-700 bg-gray-900/60 px-3 py-1.5 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      checked={superScrapeEnabled}
                      onChange={(e) => toggleSuperScrape(e.target.checked)}
                      className="h-4 w-4 shrink-0 cursor-pointer rounded-sm border-2 border-white bg-white accent-red-600"
                    />
                    <span
                      className="font-medium"
                      title="Super Scrape runs an async pipeline after lead capture: Apollo fallback (only if Seamless is exhausted), Crunchbase enrichment, Prospect Assignment, Instantly enrollment (if targeted), and CRM sync."
                    >
                      Super Scrape
                    </span>
                  </label>
                  <label className="inline-flex items-center gap-2 rounded border border-gray-700 bg-gray-900/60 px-3 py-1.5 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      checked={pepaiResearchEnabled}
                      onChange={(e) => togglePepaiResearch(e.target.checked)}
                      className="h-4 w-4 shrink-0 cursor-pointer rounded-sm border-2 border-white bg-white accent-red-600"
                    />
                    <span
                      className="font-medium"
                      title="Enable PEPai marks this lead for Instantly outreach routing after research. It passes your selected Instantly campaign and enrolls only when campaign access is allowed and the lead has a valid email plus prospect assignment."
                    >
                      Enable PEPai
                    </span>
                  </label>
                  {pepaiResearchEnabled && instantlyCampaigns.length > 0 && (
                    <select
                      value={selectedInstantlyCampaignId}
                      onChange={(e) => {
                        const nextId = e.target.value;
                        setSelectedInstantlyCampaignId(nextId);
                        safeStorageSet(PEPAI_CAMPAIGN_STORAGE_KEY, nextId);
                      }}
                      className="px-3 py-1 bg-gray-800 border border-gray-700 rounded text-sm text-white min-w-[260px]"
                    >
                      {instantlyCampaigns.map((campaign) => (
                        <option key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {canChooseGhlTarget && (
                    <select
                      value={ghlSendAsLocationId}
                      disabled={!adminToken}
                      onChange={async (e) => {
                        const nextValue = e.target.value;
                        setGhlSendAsLocationId(nextValue);
                        if (!adminToken || !userEmail) return;
                        setGhlSendAsSaving(true);
                        try {
                          await setGhlSendAsLocation({
                            token: adminToken,
                            email: userEmail,
                            ghlSendAsLocationId: nextValue || null,
                          });
                        } catch (error) {
                          console.error('Failed to save GHL send-as target', error);
                        } finally {
                          setGhlSendAsSaving(false);
                        }
                      }}
                      className="px-3 py-1 bg-gray-800 border border-gray-700 rounded text-sm text-white min-w-[280px]"
                    >
                      {!adminToken && <option value="">Admin token missing (re-login)</option>}
                      <option value="">Send to my GHL account</option>
                      {ghlTargetOptions.map((option) => (
                        <option key={option.locationId} value={option.locationId}>
                          Send to {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                  {canChooseGhlTarget && ghlSendAsSaving && (
                    <span className="text-xs text-gray-400">Saving GHL target...</span>
                  )}
                </div>
              )}
              <button
                onClick={() => setDataFieldsModalOpen(true)}
                className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded text-sm"
              >
                What You Get
              </button>
              <button
                onClick={handleResearchSelected}
                disabled={selectedCount === 0}
                className="bg-green-700 hover:bg-green-600 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
              >
                Research Selected ({selectedCount})
              </button>
              <button
                onClick={handleReResearchSelected}
                disabled={selectedCount === 0}
                className="bg-amber-600 hover:bg-amber-500 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
              >
                Re-run Research ({selectedCount})
              </button>
              <button
                onClick={handleResearchAll}
                disabled={unresearchedCount === 0}
                className="bg-green-600 hover:bg-green-500 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
              >
                Research All {unresearchedCount > 0 ? `(${unresearchedCount})` : ''}
              </button>
              <button
                onClick={handleExport}
                className="bg-gray-800 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm flex items-center gap-1"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              <button
                onClick={() => csvInputRef.current?.click()}
                className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
              >
                Import CSV
              </button>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleImportCsv(file);
                  }
                  e.currentTarget.value = '';
                }}
              />
              {csvStatus && <span className="text-xs text-gray-400">{csvStatus}</span>}
              {csvImportStatus && (
                <span className="text-xs text-gray-400">
                  {csvImportStatus}
                  {csvImportCount != null ? ` • ${csvImportCount} rows` : ''}
                  {csvImportResults != null ? ` • ${csvImportResults} results` : ''}
                </span>
              )}

              {/* Pagination */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 0}
                  className="px-2 py-1 bg-gray-800 hover:bg-gray-700 rounded text-sm disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-400">
                  Page <span className="text-white">{currentPage + 1}</span>/
                  <span className="text-white">{totalPages}</span>
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages - 1}
                  className="px-2 py-1 bg-gray-800 hover:bg-gray-700 rounded text-sm disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Results Table */}
          <div className="bg-card-dark border border-border-dark rounded-xl overflow-hidden">
            <LeadsTable
              onResearchComplete={handleResearchComplete}
              onResearchLead={handleSingleResearch}
              getDataLabel={superScrapeEnabled ? 'Super Scrape' : 'Get Data'}
              getDataTitle={superScrapeEnabled ? 'Run Super Scrape: Seamless, Apollo, Crunchbase, Prospect Assignment, CRM Delivery, and Email / Instantly Campaign Delivery' : 'Get Data'}
              contactCredits={contactCredits}
              setContactCredits={setContactCredits}
            />
          </div>
      </div>

      {/* Modals */}
      <LinkedInScrapeModal />
      <LinkedInResultsModal />
      <DataFieldsInfoModal open={dataFieldsModalOpen} onClose={() => setDataFieldsModalOpen(false)} />
      <ScheduleModal />
      <SaveSearchModal />
      <SavedSearchesDrawer />
    </div>
  );
}
